import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { MenuItem } from '@mui/material';
import List from './List';
import View from './View';
import {
  FilterBox,
  PageHeader,
  PageAction,
  PrimaryButton,
  FormButton,
  SelectFilterField,
  PrimaryPage,
} from '@/Theme';
import { LabService } from '@/Services';
import { useSnackbar } from 'notistack';
import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';
import DatePicker from "react-datepicker";
import _ from 'underscore';
import "react-datepicker/dist/react-datepicker.css";

const XLSX_ZAHL_PAYLOAD = require("xlsx/dist/xlsx.zahl");

const Home = () => {
  let navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState([]);
  const [filter, setFilter] = useState({ search: "", time: "allTime", status: ['New', 'Kit Received', 'Submitted', 'Rejected', 'Completed', 'Canceled'] });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(['New', 'Kit Received', 'Submitted', 'Rejected', 'Completed', 'Canceled']);
  const [time, setTime] = useState('allTime');
  const [count, setCount] = useState(0);
  const [size, setSize] = useState(50);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('created');
  const [direction, setDirection] = useState('desc');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  //For editing
  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawer, setDrawer] = useState('');
  const [selectedItem, setSelectedItem] = useState({});

  const { enqueueSnackbar } = useSnackbar();

  const filters = {
    where: {},
    size: size,
    page: page,
    sort: sort,
    order: direction,
  };
  const fetch = async () => {
    setLoading(true);
    filters.where = filter;
    let response = await LabService.search(filters);
    if (response && response.data && response.data.objects) {
      const objects = response.data.objects;
      setListData(objects);
      setCount(response.data.count);
    }
    setLoading(false);
  };
  useEffect(() => {
    let kn = searchParams.get("kn");
    if (kn) {
      let updatedFilter = { ...filter, ...{ search: kn } }
      setSearch(kn);
      setFilter(updatedFilter);
    }
  }, []);

  useEffect(() => {
    console.log('### fetch test types', search, size, page, sort, direction);
    filter.where = { freeText: search };
    fetch();
  }, [filter, size, page, sort, direction]);

  const onSearchChange = (e) => {
    e.preventDefault();
    console.log("set search change", e.target.value);
    setSearch(e.target.value);
  };
  const onSearchPressed = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearch(e.target.value);
      let codes = getCodes(e.target.value);
      let searchVal = codes && codes.length > 0 ? "" : search;
      let updatedFilter = {
        ...filter,
        ...{ search: searchVal },
        ...{ codes: codes },
        ...{ status: status },
        ...{ time: time }
      }
      setFilter(updatedFilter);
    }
  };
  const getCodes = (val) => {
    let codes = [];
    if (val && val.indexOf(',') > 0) {
      codes = search.split(',').map(element => element.trim().toUpperCase());
    }
    return codes;
  }
  const handleApplyFilter = () => {
    let codes = getCodes(search);
    let searchVal = codes && codes.length > 0 ? "" : search;
    let updatedFilter = {
      ...filter,
      ...{ search: searchVal },
      ...{ codes: codes },
      ...{ status: status },
      ...{ time: time }
    }
    setFilter(updatedFilter);
  }
  const onStatusSelect = (event) => {
    const { target: { value } } = event;
    setStatus(typeof value === 'string' ? value.split(',') : value,);
  }
  const onTimeSelect = (event) => {
    const { target: { value } } = event;
    setTime(value);
  }
  const handleEdit = (item) => {
    setSelectedItem(item);
    setDrawer('new');
    setOpenDrawer(true);
  };
  const handleNew = () => {
    navigate('/lab/new');
  };
  /**
   * Pull lab order 
   */
  const handlePull = () => {
    navigate('/lab/pull');
  };
  const handlePullResults = async () => {
    const response = await LabService.pullResults();
    if (response) {
      enqueueSnackbar('New results added', { variant: 'success' });
      fetch();
    }
  };
  const handlePushResults = async (testOrderId) => {
    const response = await LabService.pushResults(testOrderId);
    if (response.success) {
      enqueueSnackbar('Results have been pushed', { variant: 'success' });
      fetch();
    }
  };
  const contentDrawer = () => {
    let drawerContent = '';
    if (openDrawer) {
      if (drawer === 'view') {
        drawerContent = (
          <View
            openDrawer={openDrawer}
            setOpenDrawer={setOpenDrawer}
            selectedItem={selectedItem}
            refreshList={fetch}
            inputVariables={filter}
          />
        );
      }
    }
    return drawerContent;
  };

  const onStartChange = (date) => {
    let updatedFilter = { ...filter, ...{ startDate: date } }
    setFilter(updatedFilter);
    setStartDate(date);
  }
  const onEndChange = (date) => {
    let updatedFilter = { ...filter, ...{ endDate: date } }
    setFilter(updatedFilter);
    setEndDate(date);
  }

  const exportCSV = async () => {
    let data = [["Registration Code", "External ID", "Lab Kit", "Patient", "Status", "Last Update"]];

    let csvFilters = {
      where: filter,
      size: 99999,
      page: 1,
      sort: sort,
      order: direction
    };

    let response = await LabService.search(csvFilters);
    _.each(response.data.objects, order => {
      let row = []
      row.push(order.labOrderCode)
      row.push(order.externalId)
      row.push(order.labKit.name)
      row.push(order.patient.lastName + ', ' + order.patient.firstName)
      row.push(order.status)
      row.push(DateTime.fromISO(order.updated).toLocaleString(DateTime.DATETIME_SHORT))
      data.push(row);
    });

    let wb = XLSX.utils.book_new();
    let ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "orders.xlsx", {numbers: XLSX_ZAHL_PAYLOAD, compression: true});
  }

  return (
    <PrimaryPage title='Lab Orders'>
      <PageHeader>
        <PageAction>
          <FilterBox value={search} onChange={onSearchChange} onPress={onSearchPressed}
            extend={
              <>
                <SelectFilterField
                  labelId="filter-status-label"
                  id="filter-status-id"
                  value={status}
                  multiple
                  onChange={onStatusSelect}
                >
                  <MenuItem value="New">New</MenuItem>
                  <MenuItem value="Kit Received">Kit Received</MenuItem>
                  <MenuItem value="Submitted">Submitted</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Canceled">Canceled</MenuItem>
                </SelectFilterField>
                <SelectFilterField
                  labelId="filter-time-label"
                  id="filter-time-id"
                  value={time}
                  onChange={onTimeSelect}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="thisWeek">This Week</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                  <MenuItem value="lastMonth">Last Month</MenuItem>
                  <MenuItem value="thisYear">This Year</MenuItem>
                  <MenuItem value="allTime">All Time</MenuItem>
                </SelectFilterField>
                <FormButton compact primary onClick={handleApplyFilter}>Filter</FormButton>
              </>
          } />
          <div style={{marginLeft:20}}>
            <DatePicker placeholderText='Start Date' selected={startDate} onChange={onStartChange} />
            <DatePicker placeholderText='End Date' selected={endDate} onChange={onEndChange} />
          </div>
        </PageAction>
        <PrimaryButton onClick={handleNew}>Register Lab Order</PrimaryButton>
        <PrimaryButton onClick={handlePull}>Pull Lab Order</PrimaryButton>
        <PrimaryButton onClick={handlePullResults}>Pull Results</PrimaryButton>
        <PrimaryButton onClick={exportCSV} style={{backgroundColor:'#6458cf'}}>Export to excel</PrimaryButton>
      </PageHeader>
      <List
        listData={listData}
        setOpenDrawer={setOpenDrawer}
        handleEdit={handleEdit}
        handlePushResults={handlePushResults}
        loading={loading}
        count={count}
        size={size}
        setSize={setSize}
        page={page}
        setPage={setPage}
        sort={sort}
        setSort={setSort}
        direction={direction}
        setDirection={setDirection}
      />
      {contentDrawer()}
    </PrimaryPage>
  );
};
export default Home;
